/**
 * Noise channel wrapper.
 *
 * Loads the noise AudioWorklet module and exposes a thin API for note
 * on/off + period index (0..15) / volume / mode control. DSP lives in
 * dsp/noise.ts so it can be Vitest-tested without an AudioContext.
 */

import type { NoiseMode } from './dsp/noise';

let workletLoaded = false;

export async function loadNoiseWorklet(ctx: AudioContext): Promise<void> {
  if (workletLoaded) return;
  await ctx.audioWorklet.addModule('/worklets/noise.js');
  workletLoaded = true;
}

export interface NoiseNode {
  /** 0..15 — index into NES.NOISE_PERIOD_TABLE. 0 = highest pitch / fastest LFSR tick. */
  setPeriodIndex(index: number): void;
  setVolume(volume: number): void;
  setMode(mode: NoiseMode): void;
  /**
   * Start (or re-trigger) the channel. Pass `decayMs > 0` for a percussive
   * decay envelope (resets to full on each call). `decayMs = 0` (default)
   * keeps the noise sustained until `noteOff()`.
   */
  noteOn(decayMs?: number): void;
  noteOff(): void;
  destroy(): void;
}

export function createNoiseNode(ctx: AudioContext): NoiseNode {
  const node = new AudioWorkletNode(ctx, 'noise-processor', {
    numberOfInputs: 0,
    numberOfOutputs: 1,
    outputChannelCount: [1],
  });
  node.connect(ctx.destination);

  const set = (name: 'periodIndex' | 'volume', value: number) => {
    const p = node.parameters.get(name);
    if (p) p.value = value;
  };

  return {
    setPeriodIndex: (idx) => set('periodIndex', Math.max(0, Math.min(15, Math.round(idx)))),
    setVolume: (vol) => set('volume', vol),
    setMode: (mode) => node.port.postMessage({ type: 'setMode', mode }),
    noteOn: (decayMs = 0) => node.port.postMessage({ type: 'noteOn', decayMs }),
    noteOff: () => node.port.postMessage({ type: 'noteOff' }),
    destroy: () => {
      node.port.postMessage({ type: 'noteOff' });
      node.disconnect();
    },
  };
}
